import { BASE_ENVIRONMENT, BASE_STIMULUS_KEY, BASE_STIMULSOFT_URL, BUILD_VERSION } from "../../../utils/constant";
import { postApi, putApi } from "../../../utils/services";
import { REPORTS } from "../../../utils/services/apiPath";

const EXPECTED_STIMULSOFT_VERSION = "2026.2.2";
const STIMULSOFT_SCRIPT_MARKER = "/scripts/stimulsoft/";
const STIMULSOFT_BOOTSTRAP_PREFIX = "[Stimulsoft bootstrap]";

let hasInitializedStimulsoftLicense = false;
let hasLoggedStimulsoftDiagnostics = false;

const getGlobalStimulsoft = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.Stimulsoft;
};

const getLoadedStimulsoftScripts = () => {
  if (typeof document === "undefined") {
    return [];
  }

  return Array.from(document.scripts)
    .map(script => script.src)
    .filter(src => src.includes(STIMULSOFT_SCRIPT_MARKER))
    .map(src => {
      const url = new URL(src);
      return url.pathname.split("/").pop();
    });
};

const getStimulsoftDiagnostics = () => {
  const stimulsoft = getGlobalStimulsoft();
  const loadedScripts = getLoadedStimulsoftScripts();

  return {
    environment: BASE_ENVIRONMENT ?? "unknown",
    buildVersion: BUILD_VERSION ?? "unknown",
    licensePresent: Boolean(BASE_STIMULUS_KEY),
    licenseLength: BASE_STIMULUS_KEY?.length ?? 0,
    usingGlobalsRuntime: Boolean(stimulsoft),
    usingLegacyModuleRuntime: false,
    loadedScripts,
    versions: {
      engine: stimulsoft?.__engineVersion ?? "missing",
      reports: stimulsoft?.__reportsVersion ?? "missing",
      viewerLoaded: Boolean(stimulsoft?.Viewer?.StiViewer),
      designerLoaded: Boolean(stimulsoft?.Designer?.StiDesigner)
    }
  };
};

const logStimulsoftDiagnostics = entryPoint => {
  if (hasLoggedStimulsoftDiagnostics) {
    return;
  }

  const diagnostics = getStimulsoftDiagnostics();
  console.info(`${STIMULSOFT_BOOTSTRAP_PREFIX} ${entryPoint} ${stringifyForLog(diagnostics)}`);

  if (!diagnostics.licensePresent) {
    console.warn(`${STIMULSOFT_BOOTSTRAP_PREFIX} Missing REACT_APP_STIMULUS_KEY for ${diagnostics.environment}.`);
  }

  if (diagnostics.versions.engine !== EXPECTED_STIMULSOFT_VERSION || diagnostics.versions.reports !== EXPECTED_STIMULSOFT_VERSION) {
    console.warn(
      `${STIMULSOFT_BOOTSTRAP_PREFIX} Version mismatch detected.`,
      diagnostics.versions
    );
  }

  if (!diagnostics.loadedScripts.length) {
    console.warn(`${STIMULSOFT_BOOTSTRAP_PREFIX} No Stimulsoft public scripts were detected in the current document.`);
  }

  if ((BASE_STIMULSOFT_URL ?? "").includes("localhost")) {
    console.warn(`${STIMULSOFT_BOOTSTRAP_PREFIX} Using local ReportAPI ${BASE_STIMULSOFT_URL}. If trial persists after frontend key changes, verify the local report service license/version.`);
  }

  hasLoggedStimulsoftDiagnostics = true;
};

const ensureStimulsoftInitialized = entryPoint => {
  logStimulsoftDiagnostics(entryPoint);

  const stimulsoft = getGlobalStimulsoft();
  if (!stimulsoft?.Base?.StiLicense || !stimulsoft?.Report?.StiReport) {
    throw new Error(`${STIMULSOFT_BOOTSTRAP_PREFIX} Stimulsoft global runtime is unavailable.`);
  }

  if (!hasInitializedStimulsoftLicense) {
    stimulsoft.Base.StiLicense.key = BASE_STIMULUS_KEY ?? "";
    hasInitializedStimulsoftLicense = true;
  }

  return stimulsoft;
};

const getViewerTheme = stimulsoft => stimulsoft.Viewer.StiViewerTheme.Office2022WhiteBlue;

const getReportDefinitionText = reportFile => {
  if (typeof reportFile === "string") {
    return reportFile;
  }

  try {
    return JSON.stringify(reportFile);
  } catch {
    return "";
  }
};

const isDashboardDefinition = reportFile => {
  const definitionText = getReportDefinitionText(reportFile);
  return definitionText.includes("Stimulsoft.Dashboard.Components.StiDashboard");
};

const createStimulsoftDocument = (stimulsoft, reportFile, entryPoint) => {
  const usesDashboard = isDashboardDefinition(reportFile);
  const factoryName = usesDashboard ? "createNewDashboard" : "createNewReport";

  console.info(`${STIMULSOFT_BOOTSTRAP_PREFIX} ${entryPoint} document ${stringifyForLog({
    documentKind: usesDashboard ? "dashboard" : "report",
    factoryName
  })}`);

  return stimulsoft.Report.StiReport[factoryName]();
};

const getRuntimeReportApiBaseUrl = () => {
  if (typeof BASE_STIMULSOFT_URL !== "string") {
    return "";
  }

  return BASE_STIMULSOFT_URL.replace(/\/+$/, "");
};

const normalizePathAgainstRuntimeReportApi = value => {
  if (!value || typeof value !== "string") {
    return value ?? null;
  }

  const runtimeBaseUrl = getRuntimeReportApiBaseUrl();
  if (!runtimeBaseUrl) {
    return value;
  }

  try {
    const url = new URL(value, `${runtimeBaseUrl}/`);
    const pathname = url.pathname === "/" ? "" : url.pathname;
    return `${runtimeBaseUrl}${pathname}`;
  } catch {
    return value.startsWith("/") ? `${runtimeBaseUrl}${value}` : value;
  }
};

const sanitizeUrl = value => {
  if (!value || typeof value !== "string") {
    return value ?? null;
  }

  try {
    const baseOrigin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(value, baseOrigin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value;
  }
};

const summarizeHeaders = headers => {
  if (!Array.isArray(headers)) {
    return {
      count: 0,
      authorizationPresent: false,
      names: []
    };
  }

  const names = headers
    .map(header => header?.key ?? header?.name)
    .filter(Boolean);

  return {
    count: headers.length,
    authorizationPresent: names.includes("Authorization"),
    names
  };
};

const summarizeProcessDataEvent = event => ({
  action: event?.action ?? null,
  command: event?.command ?? null,
  connectionString: sanitizeUrl(event?.connectionString),
  pathData: sanitizeUrl(event?.pathData),
  dataSource: event?.dataSource ?? null,
  hasReport: Boolean(event?.report),
  headers: summarizeHeaders(event?.headers),
  eventKeys: Object.keys(event ?? {}).slice(0, 20)
});

const stringifyForLog = value => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const logProcessDataEvent = (entryPoint, stage, event) => {
  console.info(`${STIMULSOFT_BOOTSTRAP_PREFIX} ${entryPoint} ${stage} ${stringifyForLog(summarizeProcessDataEvent(event))}`);
};

const summarizeReportDictionary = report => {
  const databases = report?.dictionary?.databases?.list ?? [];

  return databases.map(database => ({
    name: database?.name ?? null,
    alias: database?.alias ?? null,
    connectionString: sanitizeUrl(database?.connectionString),
    pathData: sanitizeUrl(database?.pathData ?? database?.path),
    headers: summarizeHeaders(database?.headers)
  }));
};

const logReportDictionary = (entryPoint, report) => {
  console.info(`${STIMULSOFT_BOOTSTRAP_PREFIX} ${entryPoint} dictionary ${stringifyForLog(summarizeReportDictionary(report))}`);
};

const upsertAuthorizationHeader = (headers, accessToken) => {
  const safeHeaders = Array.isArray(headers) ? headers : [];
  const authorizationValue = `Bearer ${accessToken}`;
  const existingHeader = safeHeaders.find(header => String(header?.key ?? header?.name ?? "").toLowerCase() === "authorization");

  if (existingHeader) {
    existingHeader.value = authorizationValue;
    return safeHeaders;
  }

  safeHeaders.push({ key: "Authorization", value: authorizationValue });
  return safeHeaders;
};

const stripAuthorizationHeaders = headers => {
  if (!Array.isArray(headers)) {
    return [];
  }

  return headers.filter(header => String(header?.key ?? header?.name ?? "").toLowerCase() !== "authorization");
};

const applyRuntimeProcessDataContext = ({
  event,
  accessToken,
  pathData,
  normalizeExistingPathData = false,
  dataSource
}) => {
  event.connectionString = getRuntimeReportApiBaseUrl();

  if (typeof dataSource === "string") {
    event.dataSource = dataSource;
  }

  if (typeof pathData === "string") {
    event.pathData = normalizePathAgainstRuntimeReportApi(pathData);
  } else if (normalizeExistingPathData && event?.pathData) {
    event.pathData = normalizePathAgainstRuntimeReportApi(event.pathData);
  }

  const runtimeHeaders = stripAuthorizationHeaders(event?.headers);
  event.headers = accessToken ? upsertAuthorizationHeader(runtimeHeaders, accessToken) : runtimeHeaders;
};

const normalizeReportDictionaryForRuntime = report => {
  const databases = report?.dictionary?.databases?.list ?? [];
  const runtimeBaseUrl = getRuntimeReportApiBaseUrl();

  databases.forEach(database => {
    if (!database || typeof database !== "object") {
      return;
    }

    if (typeof database.connectionString === "string" && runtimeBaseUrl) {
      database.connectionString = runtimeBaseUrl;
    }

    if (typeof database.pathData === "string" && database.pathData.length > 0) {
      database.pathData = normalizePathAgainstRuntimeReportApi(database.pathData);
    }

    if (typeof database.path === "string" && database.path.length > 0) {
      database.path = normalizePathAgainstRuntimeReportApi(database.path);
    }

    database.headers = stripAuthorizationHeaders(database.headers);
  });

  return report;
};

/**
 * Replace {filterText} placeholders in all OData data source SQL commands with the
 * actual filter string. Stimulsoft resolves {filterText} from dictionary variables
 * before onBeginProcessData fires, so variable-based approaches fail if the MRT
 * template lacks the filterText declaration. This modifies the sources directly
 * after the report is loaded but before rendering.
 */
const injectFiltersIntoDataSources = (report, filters) => {
  if (typeof filters !== "string" || filters.length === 0) {
    return;
  }

  const dataSources = report?.dictionary?.dataSources?.list;
  if (!Array.isArray(dataSources)) {
    return;
  }

  dataSources.forEach(ds => {
    if (ds && typeof ds.sqlCommand === "string" && ds.sqlCommand.includes("{filterText}")) {
      // Use regex global replace with function replacer to:
      // 1. Replace all occurrences (not just first)
      // 2. Avoid $ sequences in filters being interpreted as replacement tokens
      ds.sqlCommand = ds.sqlCommand.replace(/\{filterText\}/g, () => filters);
    }
  });
};

const ODataReport = class {
  constructor({ width = "100%", height = "100vh", theme, reportFile, accessToken }) {
    const stimulsoft = ensureStimulsoftInitialized("ODataReport");
    const viewerTheme = theme ?? getViewerTheme(stimulsoft);

    this.viewerOptions = new stimulsoft.Viewer.StiViewerOptions();
    this.viewerOptions.width = width;
    this.viewerOptions.height = height;
    this.viewerOptions.appearance.theme = viewerTheme;
    this.viewer = new stimulsoft.Viewer.StiViewer(null, "StiViewer", false);
    this.viewer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken });
      logProcessDataEvent("ODataReport viewer", "begin", event);
    };
    this.viewer.onEndProcessData = event => logProcessDataEvent("ODataReport viewer", "end", event);

    this.report = createStimulsoftDocument(stimulsoft, reportFile, "ODataReport");
    this.report.loadFile(reportFile);
    normalizeReportDictionaryForRuntime(this.report);
    logReportDictionary("ODataReport", this.report);
    this.viewer.report = this.report;
  }

  viewer = () => {
    return this.viewer;
  };
};

const ODataReportS3 = class {
  constructor({
    width = "100%",
    height = "100vh",
    theme,
    reportFile,
    accessToken,
    filters,
    reportId,
    reportCategoryId,
    fromTemplate,
    reportName,
    filePath,
    isDefault,
    standardReportFilters
  }) {
    const stimulsoft = ensureStimulsoftInitialized("ODataReportS3");
    const viewerTheme = theme ?? getViewerTheme(stimulsoft);

    this.viewerOptions = new stimulsoft.Viewer.StiViewerOptions();
    this.viewerOptions.width = width;
    this.viewerOptions.height = height;
    this.viewerOptions.appearance.theme = viewerTheme;
    this.viewerOptions.appearance.fullScreenMode = true;
    this.viewerOptions.appearance.scrollbarsMode = true;
    this.viewerOptions.toolbar.viewMode = stimulsoft.Viewer.StiWebViewMode.Continuous;
    this.viewer = new stimulsoft.Viewer.StiViewer(this.viewerOptions, "StiViewer", false);
    this.viewer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, dataSource: "" });
      logProcessDataEvent("ODataReportS3 viewer", "begin", event);
    };
    this.viewer.onEndProcessData = event => logProcessDataEvent("ODataReportS3 viewer", "end", event);

    this.report = createStimulsoftDocument(stimulsoft, reportFile, "ODataReportS3");
    this.report.saveTo;
    this.report.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, dataSource: "" });
      logProcessDataEvent("ODataReportS3 report", "begin", event);
    };
    this.report.load(reportFile);
    normalizeReportDictionaryForRuntime(this.report);
    injectFiltersIntoDataSources(this.report, filters);
    logReportDictionary("ODataReportS3", this.report);
    this.viewer.report = this.report;

    let options = new stimulsoft.Designer.StiDesignerOptions();
    options.appearance.fullScreenMode = true;

    this.designer = new stimulsoft.Designer.StiDesigner(options, "StiDesigner", false);
    this.designer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, dataSource: "" });
      logProcessDataEvent("ODataReportS3 designer", "begin", event);
    };
    this.designer.onEndProcessData = event => logProcessDataEvent("ODataReportS3 designer", "end", event);
    this.designer.onSaveReport = async function (args) {
      args.preventDefault = true;
      let report = args.report;
      normalizeReportDictionaryForRuntime(report);
      logReportDictionary("ODataReportS3 save", report);
      const jsonString = report.saveToJsonString();
      const apiMethod = !isDefault ? putApi : postApi;
      const url = !isDefault ? `${REPORTS}/${reportId}` : REPORTS;

      const encoder = new TextEncoder();
      const byteArray = encoder.encode(jsonString);
      let base64Report = stimulsoft.System.Convert.toBase64String(byteArray);
      const { apprenticeFilter, dateFilter, mentorFilter, partnerFilter, projectFilter } = standardReportFilters;
      try {
        await apiMethod(url, {
          dataType: "odata",
          paramString: "",
          reportCategoryId: reportCategoryId,
          reportName: reportName,
          apprenticeFilter: apprenticeFilter,
          ddateFilter: dateFilter,
          mentorFilter: mentorFilter,
          partnerFilter: partnerFilter,
          projectFilter: projectFilter,
          reportFile: {
            base64String: base64Report,
            fileName: filePath.replace("/", "")
          }
        });
        if (isDefault) {
          alert("Successfuly created new custom report! Please switch to that report to continue editing.");
        }
      } catch (e) {
        console.log(e);
      }
    };
    this.designer.report = this.report;
  }

  viewer = () => {
    return this.viewer;
  };

  designer = () => {
    return this.designer;
  };
};

const HTTPReportS3 = class {
  constructor({
    width = "100%",
    height = "100vh",
    theme,
    reportFile,
    accessToken,
    filters,
    reportId,
    reportCategoryId,
    fromTemplate,
    reportName,
    filePath,
    isDefault,
    standardReportFilters
  }) {
    const stimulsoft = ensureStimulsoftInitialized("HTTPReportS3");
    const viewerTheme = theme ?? getViewerTheme(stimulsoft);

    this.viewerOptions = new stimulsoft.Viewer.StiViewerOptions();
    this.viewerOptions.width = width;
    this.viewerOptions.height = height;
    this.viewerOptions.appearance.theme = viewerTheme;
    this.viewerOptions.appearance.fullScreenMode = true;
    this.viewerOptions.appearance.scrollbarsMode = true;
    this.viewerOptions.toolbar.viewMode = stimulsoft.Viewer.StiWebViewMode.Continuous;
    this.viewer = new stimulsoft.Viewer.StiViewer(null, "StiViewer", false);
    this.viewer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, normalizeExistingPathData: true });
      logProcessDataEvent("HTTPReportS3 viewer", "begin", event);
    };
    this.viewer.onEndProcessData = event => logProcessDataEvent("HTTPReportS3 viewer", "end", event);

    this.report = createStimulsoftDocument(stimulsoft, reportFile, "HTTPReportS3");
    this.report.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, normalizeExistingPathData: true });
      logProcessDataEvent("HTTPReportS3 report", "begin", event);
    };
    this.report.load(reportFile);
    normalizeReportDictionaryForRuntime(this.report);
    logReportDictionary("HTTPReportS3", this.report);
    this.viewer.report = this.report;

    let options = new stimulsoft.Designer.StiDesignerOptions();
    options.appearance.fullScreenMode = true;

    this.designer = new stimulsoft.Designer.StiDesigner(options, "StiDesigner", false);
    this.designer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, filters, normalizeExistingPathData: true });
      logProcessDataEvent("HTTPReportS3 designer", "begin", event);
    };
    this.designer.onEndProcessData = event => logProcessDataEvent("HTTPReportS3 designer", "end", event);

    this.designer.onSaveReport = async function (args) {
      args.preventDefault = true;
      let report = args.report;
      normalizeReportDictionaryForRuntime(report);
      logReportDictionary("HTTPReportS3 save", report);
      const jsonString = report.saveToJsonString();
      const apiMethod = !isDefault ? putApi : postApi;
      const url = !isDefault ? `${REPORTS}/${reportId}` : REPORTS;
      const { apprenticeFilter, dateFilter, mentorFilter, partnerFilter, projectFilter } = standardReportFilters;

      const encoder = new TextEncoder();
      const byteArray = encoder.encode(jsonString);
      let base64Report = stimulsoft.System.Convert.toBase64String(byteArray);
      try {
        await apiMethod(url, {
          dataType: "json",
          paramString: "",
          reportCategoryId: reportCategoryId,
          reportName: reportName,
          apprenticeFilter: apprenticeFilter,
          ddateFilter: dateFilter,
          mentorFilter: mentorFilter,
          partnerFilter: partnerFilter,
          projectFilter: projectFilter,
          reportFile: {
            base64String: base64Report,
            fileName: filePath.replace("/", "")
          }
        });
        if (isDefault) {
          alert("Successfuly created new custom report! Please switch to that report to continue editing.");
        }
      } catch (e) {
        console.log(e);
      }
    };
    this.designer.report = this.report;
  }

  viewer = () => {
    return this.viewer;
  };

  designer = () => {
    return this.designer;
  };
};

const HTTPReport = class {
  constructor({ width = "100%", height = "100vh", theme, path, reportFile, accessToken }) {
    const stimulsoft = ensureStimulsoftInitialized("HTTPReport");
    const viewerTheme = theme ?? getViewerTheme(stimulsoft);

    this.viewerOptions = new stimulsoft.Viewer.StiViewerOptions();
    this.viewerOptions.width = width;
    this.viewerOptions.height = height;
    this.viewerOptions.appearance.theme = viewerTheme;
    this.viewer = new stimulsoft.Viewer.StiViewer(null, "StiViewer", false);
    this.viewer.onBeginProcessData = event => {
      applyRuntimeProcessDataContext({ event, accessToken, pathData: path });
      logProcessDataEvent("HTTPReport viewer", "begin", event);
    };
    this.viewer.onEndProcessData = event => logProcessDataEvent("HTTPReport viewer", "end", event);

    this.report = createStimulsoftDocument(stimulsoft, reportFile, "HTTPReport");
    this.report.loadFile(reportFile);
    normalizeReportDictionaryForRuntime(this.report);
    logReportDictionary("HTTPReport", this.report);
    this.viewer.report = this.report;
  }

  viewer = () => {
    return this.viewer;
  };
};

export { ODataReport, HTTPReport, ODataReportS3, HTTPReportS3 };
